/**
 * Measurement form — four numeric inputs (AP length, right/left diagonals,
 * parietal width) and a Calculate button. The form is the primary input
 * surface of the calculator.
 *
 * State contract:
 *   - Reads `state.flow` to determine display:
 *       ready/filling → normal
 *       invalid       → per-field error messages, error styling
 *       calculating   → button disabled + "Calculating…" label
 *       results       → button label "Recalculate"
 *       generating-pdf / pdf-error → form fields disabled (don't let user
 *         scramble input values mid-PDF generation)
 *   - Dispatches `set-measurement` on each input event
 *   - Dispatches `calculate-requested` on form submit
 *
 * Inputs are uncontrolled at the cursor level — we only set `.value` from state
 * when the value actually differs (preserves cursor position on re-renders).
 *
 * Field choice: `type="text" inputmode="decimal"` rather than `type="number"`.
 * Number inputs swallow non-digit keystrokes inconsistently across browsers
 * and lose focus on iOS when the value is rejected. Decimal inputmode shows
 * the right keyboard on iPad without browser-level value mangling.
 */

import { el } from "../dom";
import type { AppState, FieldErrors, FormValues } from "../state";
import type { Action } from "../state";

import "./form.css";

export interface FormDeps {
  dispatch: (action: Action) => void;
}

export interface FormHandle {
  el: HTMLElement;
  update: (state: AppState) => void;
}

interface FieldDef {
  key: keyof FormValues;
  label: string;
  hint: string;
}

const FIELDS: readonly FieldDef[] = [
  {
    key: "apLength",
    label: "AP length (mm)",
    hint: "Front-to-back: glabella to opisthocranion",
  },
  {
    key: "rightDiagonal",
    label: "Right diagonal (mm)",
    hint: "Right-front to left-back, 30° from midline",
  },
  {
    key: "leftDiagonal",
    label: "Left diagonal (mm)",
    hint: "Left-front to right-back, 30° from midline",
  },
  {
    key: "parietalWidth",
    label: "Parietal width (mm)",
    hint: "Side-to-side at widest point above the ears",
  },
];

interface FieldRefs {
  input: HTMLInputElement;
  errorEl: HTMLElement;
  wrap: HTMLElement;
}

export function createForm(deps: FormDeps): FormHandle {
  const fieldRefs = new Map<keyof FormValues, FieldRefs>();

  // ---- Baby's name input — optional, lives above the measurements ----
  // Doesn't get the same FieldRefs treatment as the numeric fields because
  // it has no error path (nothing to validate; empty falls back to "Your baby"
  // in PDF + on-screen variant text).
  const babyNameInput = el("input", {
    id: "field-babyName",
    class: "form__input",
    type: "text",
    autocomplete: "off",
    spellcheck: false,
    "aria-describedby": "field-babyName-hint",
    oninput: (event: Event) => {
      const target = event.target as HTMLInputElement;
      deps.dispatch({
        type: "set-measurement",
        field: "babyName",
        value: target.value,
      });
    },
  });

  const babyNameField = el(
    "div",
    { class: "form__field form__field--name" },
    el(
      "label",
      { class: "form__label", for: "field-babyName" },
      "Baby's name ",
      el("span", { class: "form__optional" }, "(optional)"),
    ),
    el(
      "p",
      { id: "field-babyName-hint", class: "form__hint" },
      "Appears on the PDF handout. Skip to use a generic substitute.",
    ),
    babyNameInput,
  );

  const fieldEls = FIELDS.map((field) => {
    const inputId = `field-${field.key}`;
    const errorId = `${inputId}-error`;

    const input = el("input", {
      id: inputId,
      class: "form__input",
      type: "text",
      inputmode: "decimal",
      autocomplete: "off",
      spellcheck: false,
      "aria-describedby": `${inputId}-hint ${errorId}`,
      oninput: (event: Event) => {
        const target = event.target as HTMLInputElement;
        deps.dispatch({
          type: "set-measurement",
          field: field.key,
          value: target.value,
        });
      },
    });

    const errorEl = el("p", {
      id: errorId,
      class: "form__error",
      role: "alert",
      "aria-live": "polite",
    });

    const wrap = el(
      "div",
      { class: "form__field" },
      el("label", { class: "form__label", for: inputId }, field.label),
      el("p", { id: `${inputId}-hint`, class: "form__hint" }, field.hint),
      input,
      errorEl,
    );

    fieldRefs.set(field.key, { input, errorEl, wrap });
    return wrap;
  });

  const button = el("button", { type: "submit", class: "form__submit" }, "Calculate");

  const root = el(
    "form",
    {
      class: "form",
      "aria-label": "Measurement entry",
      novalidate: true,
      onsubmit: (event: Event) => {
        event.preventDefault();
        deps.dispatch({ type: "calculate-requested" });
      },
    },
    babyNameField,
    el(
      "fieldset",
      { class: "form__fieldset" },
      el("legend", { class: "form__legend" }, "Measurements"),
      ...fieldEls,
    ),
    button,
  );

  function update(state: AppState): void {
    const flow = state.flow;

    // Sync per-field input values from state. We only assign `.value` when it
    // differs from the DOM's current value, which preserves the user's cursor
    // position. Important on backspace + retype.
    const values: FormValues | null = flow.tag === "ready" ? null : flow.values;

    // Baby name follows the same cursor-preserving pattern; no error path.
    const desiredName = values ? values.babyName : "";
    if (babyNameInput.value !== desiredName) {
      babyNameInput.value = desiredName;
    }
    babyNameInput.disabled = flow.tag === "generating-pdf" || flow.tag === "pdf-error";

    const errors: FieldErrors = flow.tag === "invalid" ? flow.errors : {};

    for (const field of FIELDS) {
      const refs = fieldRefs.get(field.key);
      if (!refs) continue;
      const desired = values ? values[field.key] : "";
      if (refs.input.value !== desired) {
        refs.input.value = desired;
      }
      const errorMsg = errors[field.key];
      if (errorMsg !== undefined && errorMsg !== "") {
        refs.errorEl.textContent = errorMsg;
        refs.wrap.classList.add("form__field--error");
        refs.input.setAttribute("aria-invalid", "true");
      } else {
        refs.errorEl.textContent = "";
        refs.wrap.classList.remove("form__field--error");
        refs.input.removeAttribute("aria-invalid");
      }
    }

    // Disable inputs while a PDF is generating. Don't disable on `calculating`
    // — that's a 200ms blip the UX shouldn't notice.
    const inputsDisabled = flow.tag === "generating-pdf" || flow.tag === "pdf-error";
    for (const refs of fieldRefs.values()) {
      refs.input.disabled = inputsDisabled;
    }

    // Button label/state.
    switch (flow.tag) {
      case "calculating":
        button.textContent = "Calculating…";
        button.disabled = true;
        break;
      case "results":
        button.textContent = "Recalculate";
        button.disabled = false;
        break;
      case "generating-pdf":
      case "pdf-error":
        button.textContent = "Recalculate";
        button.disabled = true;
        break;
      default:
        button.textContent = "Calculate";
        button.disabled = false;
    }
  }

  return { el: root, update };
}
